/**
 * Use of force reports, personnel actions (write-ups, commendations, complaints), my record,
 * and the early warning system.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api } from '../shared/api.ts';
import { useAuth } from '../shared/auth.tsx';
import { useRouter } from '../shared/router.tsx';
import { Badge, Card, ErrorBox, Notice, PageHeader, Spinner, useAction, useLoad } from '../shared/ui.tsx';
import { at, day, EmptyState, KPI, when } from './common.tsx';

type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'info';
const crumb = (label: string, to: string, navigate: (p: string) => void) => (
  <button className="btn link" onClick={() => navigate(at(to))}>
    {label}
  </button>
);
const localInput = (iso: string | null | undefined) => {
  const d = iso ? new Date(iso) : new Date();
  return new Date(+d - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

// ─── Use of force ───────────────────────────────────────────────────────────
const UOF_STATUS: Record<string, [string, Tone]> = { draft: ['Draft', 'neutral'], submitted: ['Waiting for supervisor', 'warn'], returned: ['Sent back', 'bad'], supervisor_reviewed: ['Waiting for command', 'info'], closed: ['Closed', 'ok'] };

export function UofList() {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const [scope, setScope] = useState<'mine' | 'review' | 'command' | 'all'>('mine');
  const r = useLoad(() => api.get<any>(`/api/uof?scope=${scope}`), [scope]);
  const a = useAction();
  const start = () => a.run(async () => navigate(at(`/uof/${(await api.post<any>('/api/uof', {})).id}`)));
  const tabs = (['mine', ...(r.data?.canReview ? ['review'] : []), ...(r.data?.canClose ? ['command'] : []), ...(r.data?.canReview || r.data?.canClose ? ['all'] : [])] as const) as readonly ('mine' | 'review' | 'command' | 'all')[];
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Records"
        title="Use of force"
        sub="Response to resistance: the officer reports, the supervisor reviews, command staff close it."
        actions={
          (can('reports.write') || can('reports.approve')) && (
            <button className="btn primary" disabled={a.busy} onClick={start}>
              New use of force report
            </button>
          )
        }
      />
      <ErrorBox error={a.error} />
      {r.data && (r.data.canReview || r.data.canClose) && (
        <div className="ops-kpis">
          {r.data.canReview && <KPI n={r.data.counts.review} label="Waiting for supervisor" tone={r.data.counts.review ? 'warn' : undefined} onClick={() => setScope('review')} on={scope === 'review'} />}
          {r.data.canClose && <KPI n={r.data.counts.command} label="Waiting for command" tone={r.data.counts.command ? 'warn' : undefined} onClick={() => setScope('command')} on={scope === 'command'} />}
        </div>
      )}
      <div className="seg-ctl" role="tablist" aria-label="Which reports">
        {tabs.map((k) => (
          <button key={k} type="button" role="tab" aria-selected={scope === k} className={scope === k ? 'on' : ''} onClick={() => setScope(k)}>
            {{ mine: 'Mine', review: 'To review', command: 'Command review', all: 'All' }[k]}
          </button>
        ))}
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.reports.length ? (
        <EmptyState icon="report" title="No use of force reports here" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Report</th>
                <th>When</th>
                <th>Officer</th>
                <th>Force</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.reports.map((x: any) => (
                <tr key={x.id} className="clickable" onClick={() => navigate(at(`/uof/${x.id}`))}>
                  <td className="mono">
                    {x.number}
                    {x.serious && (
                      <div>
                        <Badge tone="bad">Serious</Badge>
                      </div>
                    )}
                  </td>
                  <td>{when(x.occurred_at)}</td>
                  <td>{x.officer_name}</td>
                  <td className="small">{(x.force_types ?? []).map((f: string) => r.data.forceTypes[f]?.split(' (')[0] ?? f).join(', ') || '—'}</td>
                  <td>
                    <Badge tone={UOF_STATUS[x.status]![1]}>{UOF_STATUS[x.status]![0]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const blankSubject = { name: '', dob: '', race: '', sex: '', height: '', weight: '', impairment: '', armed: '', resistance: 'active', injured: false, injury: '', treatment: '', arrested: false, charges: '', hospitalized: false, died: false };

export function UofReport({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/uof/${id}`), [id]);
  const people = useLoad(() => api.get<any>('/api/mail/people').catch(() => ({ people: [] })), []);
  const a = useAction();
  const [f, setF] = useState<any>(null);
  const [note, setNote] = useState('');
  const [finding, setFinding] = useState('within_policy');
  const v = r.data;
  useEffect(() => {
    if (v && !f) {
      const x = v.report;
      setF({
        occurredAt: localInput(x.occurred_at), location: x.location ?? '', caseNumber: x.case_number ?? '', cadNumber: x.cad_number ?? '', contactReason: x.contact_reason ?? '',
        subjects: x.subjects?.length ? x.subjects : [{ ...blankSubject }], forceTypes: x.force_types ?? [], details: x.details ?? {}, officerInjured: x.officer_injured, officerInjury: x.officer_injury ?? '',
        otherOfficers: x.other_officers ?? [], intervention: x.intervention ?? '', bwc: x.bwc ?? '', narrative: x.narrative ?? '',
      });
    }
  }, [v, f]);
  if (!v || !f) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const x = v.report;
  const L = v.lists;
  const payload = () => ({ ...f, occurredAt: f.occurredAt ? new Date(f.occurredAt).toISOString() : null });
  const save = () => a.run(async () => (await api.post(`/api/uof/${id}`, payload()), r.reload()));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => (await api.post(`/api/uof/${id}`, payload()), await api.post(`/api/uof/${id}/submit`, {}), setF(null), r.reload()));
  };
  const review = (ret: boolean) => a.run(async () => (await api.post(`/api/uof/${id}/review`, ret ? { return: true, note } : { finding, note: note || null }), setNote(''), r.reload()));
  const close = () => a.run(async () => (await api.post(`/api/uof/${id}/close`, { finding, note: note || null }), setNote(''), r.reload()));
  const setS = (i: number, k: string, val: unknown) => setF({ ...f, subjects: f.subjects.map((s: any, j: number) => (j === i ? { ...s, [k]: val } : s)) });
  const toggleForce = (k: string) => setF({ ...f, forceTypes: f.forceTypes.includes(k) ? f.forceTypes.filter((z: string) => z !== k) : [...f.forceTypes, k] });
  const ro = !v.canEdit;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Use of force', '/uof', navigate)} title={`${x.number} · Use of force`} sub={`${x.officer_name}${x.officer_badge ? ` (${x.officer_badge})` : ''}`} actions={<Badge tone={UOF_STATUS[x.status]![1]}>{UOF_STATUS[x.status]![0]}</Badge>} />
      <ErrorBox error={a.error} />
      {x.status === 'returned' && <Notice tone="bad">Sent back: {v.events.filter((e: any) => e.action === 'returned').at(-1)?.note}</Notice>}
      <form className="stack" onSubmit={submit}>
        <fieldset disabled={ro} className="uof-fs stack">
          <Card title="Incident">
            <div className="grid-3">
              <label>
                When
                <input type="datetime-local" value={f.occurredAt} onChange={(e) => setF({ ...f, occurredAt: e.target.value })} />
              </label>
              <label>
                Where *
                <input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} />
              </label>
              <label>
                Reason for contact
                <select value={f.contactReason} onChange={(e) => setF({ ...f, contactReason: e.target.value })}>
                  <option value="">Choose…</option>
                  {L.contactReasons.map((c: string) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>
              <label>
                Case #
                <input value={f.caseNumber} onChange={(e) => setF({ ...f, caseNumber: e.target.value })} />
              </label>
              <label>
                CAD #
                <input value={f.cadNumber} onChange={(e) => setF({ ...f, cadNumber: e.target.value })} />
              </label>
              <label>
                Body camera
                <input value={f.bwc} onChange={(e) => setF({ ...f, bwc: e.target.value })} placeholder="Activated? Recording IDs" />
              </label>
            </div>
          </Card>
          {f.subjects.map((s: any, i: number) => (
            <Card key={i} title={`Subject ${i + 1}`}>
              <div className="stack sm">
                <div className="grid-4">
                  <label>
                    Name
                    <input value={s.name ?? ''} onChange={(e) => setS(i, 'name', e.target.value)} />
                  </label>
                  <label>
                    DOB
                    <input value={s.dob ?? ''} onChange={(e) => setS(i, 'dob', e.target.value)} placeholder="MM/DD/YYYY" />
                  </label>
                  <label>
                    Race / sex
                    <input value={`${s.race ?? ''}${s.sex ? `/${s.sex}` : ''}`} onChange={(e) => {
                      const [race, sex] = e.target.value.split('/');
                      setF({ ...f, subjects: f.subjects.map((z: any, j: number) => (j === i ? { ...z, race: race ?? '', sex: sex ?? '' } : z)) });
                    }} placeholder="W/M" />
                  </label>
                  <label>
                    Resistance
                    <select value={s.resistance ?? 'active'} onChange={(e) => setS(i, 'resistance', e.target.value)}>
                      <option value="passive">Passive</option>
                      <option value="active">Active</option>
                      <option value="aggressive">Aggressive / assaultive</option>
                      <option value="deadly">Deadly force threat</option>
                    </select>
                  </label>
                  <label>
                    Impaired by
                    <input value={s.impairment ?? ''} onChange={(e) => setS(i, 'impairment', e.target.value)} placeholder="Alcohol, drugs, mental health…" />
                  </label>
                  <label>
                    Armed with
                    <input value={s.armed ?? ''} onChange={(e) => setS(i, 'armed', e.target.value)} />
                  </label>
                  <label>
                    Charges
                    <input value={s.charges ?? ''} onChange={(e) => setS(i, 'charges', e.target.value)} />
                  </label>
                  <label>
                    Injury
                    <input value={s.injury ?? ''} onChange={(e) => setS(i, 'injury', e.target.value)} />
                  </label>
                </div>
                <div className="row gap wrap">
                  {(
                    [
                      ['arrested', 'Arrested'],
                      ['injured', 'Injured'],
                      ['hospitalized', 'Hospitalized'],
                      ['died', 'Died'],
                    ] as const
                  ).map(([k, l]) => (
                    <label key={k} className="check">
                      <input type="checkbox" checked={!!s[k]} onChange={(e) => setS(i, k, e.target.checked)} /> {l}
                    </label>
                  ))}
                </div>
              </div>
            </Card>
          ))}
          {!ro && (
            <div>
              <button type="button" className="btn small" onClick={() => setF({ ...f, subjects: [...f.subjects, { ...blankSubject }] })}>
                + Another subject
              </button>
            </div>
          )}
          <Card title="Force used *">
            <div className="uof-force">
              {Object.entries(L.forceTypes).map(([k, l]) => (
                <label key={k} className={`check uof-ft${k === 'firearm_discharged' ? ' bad' : ''}`}>
                  <input type="checkbox" checked={f.forceTypes.includes(k)} onChange={() => toggleForce(k)} /> {l as string}
                </label>
              ))}
            </div>
            {f.forceTypes.some((k: string) => k.startsWith('cew')) && (
              <div className="grid-3" style={{ marginTop: 10 }}>
                <label>
                  Taser cycles
                  <input value={f.details.cewCycles ?? ''} onChange={(e) => setF({ ...f, details: { ...f.details, cewCycles: e.target.value } })} />
                </label>
                <label>
                  Probes hit
                  <input value={f.details.cewProbesHit ?? ''} onChange={(e) => setF({ ...f, details: { ...f.details, cewProbesHit: e.target.value } })} />
                </label>
              </div>
            )}
            {f.forceTypes.includes('firearm_discharged') && (
              <div className="grid-3" style={{ marginTop: 10 }}>
                <label>
                  Rounds fired
                  <input value={f.details.roundsFired ?? ''} onChange={(e) => setF({ ...f, details: { ...f.details, roundsFired: e.target.value } })} />
                </label>
                <label>
                  Distance
                  <input value={f.details.distance ?? ''} onChange={(e) => setF({ ...f, details: { ...f.details, distance: e.target.value } })} />
                </label>
                <label>
                  Lighting
                  <input value={f.details.lighting ?? ''} onChange={(e) => setF({ ...f, details: { ...f.details, lighting: e.target.value } })} />
                </label>
              </div>
            )}
          </Card>
          <Card title="Officers">
            <div className="stack sm">
              <div className="row gap wrap">
                <label className="check">
                  <input type="checkbox" checked={f.officerInjured} onChange={(e) => setF({ ...f, officerInjured: e.target.checked })} /> I was injured
                </label>
                {f.officerInjured && <input value={f.officerInjury} onChange={(e) => setF({ ...f, officerInjury: e.target.value })} placeholder="Injury and treatment" aria-label="Officer injury" style={{ flex: 1 }} />}
              </div>
              <label>
                Other officers present
                <select multiple value={f.otherOfficers} onChange={(e) => setF({ ...f, otherOfficers: [...e.target.selectedOptions].map((o) => o.value) })} style={{ minHeight: 90 }}>
                  {(people.data?.people ?? []).map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Duty to intervene — did anyone need to, or did anyone, intervene?
                <input value={f.intervention} onChange={(e) => setF({ ...f, intervention: e.target.value })} placeholder="No intervention needed / describe" />
              </label>
            </div>
          </Card>
          <Card title="Narrative *">
            <textarea rows={10} value={f.narrative} onChange={(e) => setF({ ...f, narrative: e.target.value })} aria-label="Narrative" placeholder="What the subject did, what you did and why, in order." />
          </Card>
        </fieldset>
        {v.canEdit && (
          <div className="row gap">
            <button className="btn primary" disabled={a.busy}>
              Submit to my supervisor
            </button>
            <button type="button" className="btn" disabled={a.busy} onClick={save}>
              Save draft
            </button>
          </div>
        )}
      </form>
      {x.sup_finding && (
        <Card title="Supervisor review">
          <p>
            <strong>{L.findings[x.sup_finding]}</strong> — {x.sup_name}, {when(x.sup_at)}
          </p>
          {x.sup_note && <p className="pre small">{x.sup_note}</p>}
        </Card>
      )}
      {x.cmd_finding && (
        <Card title="Command review">
          <p>
            <strong>{L.findings[x.cmd_finding]}</strong> — {x.cmd_name}, {when(x.cmd_at)}
          </p>
          {x.cmd_note && <p className="pre small">{x.cmd_note}</p>}
        </Card>
      )}
      {(v.canReview || v.canClose) && (
        <Card title={v.canReview ? 'Supervisor review' : 'Command review'}>
          <div className="stack sm">
            <label>
              Finding
              <select value={finding} onChange={(e) => setFinding(e.target.value)}>
                {Object.entries(L.findings).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l as string}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Notes
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
            <div className="row gap">
              <button className="btn primary" disabled={a.busy} onClick={() => (v.canReview ? review(false) : close())}>
                {v.canReview ? 'Send to command staff' : 'Close the review'}
              </button>
              {v.canReview && (
                <button className="btn warn" disabled={a.busy || !note.trim()} onClick={() => review(true)}>
                  Send back to the officer
                </button>
              )}
            </div>
          </div>
        </Card>
      )}
      <Card title="History">
        <ul className="pio-hist">
          {v.events.map((e: any, i: number) => (
            <li key={i}>
              <strong>{e.action.charAt(0).toUpperCase() + e.action.slice(1)}</strong>
              <span className="small muted">
                {' '}
                · {e.by_name} · {when(e.at)}
              </span>
              {e.note && <div className="small pre">{e.note}</div>}
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

// ─── Personnel actions ──────────────────────────────────────────────────────
const PA_STATUS: Record<string, [string, Tone]> = { draft: ['Draft', 'neutral'], issued: ['Issued — not acknowledged', 'warn'], acknowledged: ['Acknowledged', 'ok'], void: ['Void', 'neutral'] };
const kindTone = (k: any): Tone => (k?.tone === 'good' ? 'ok' : k?.tone === 'bad' ? 'bad' : 'info');

export function PersonnelList() {
  const { navigate } = useRouter();
  const [employee, setEmployee] = useState(new URLSearchParams(location.search).get('employee') ?? '');
  const [kind, setKind] = useState('');
  const [adding, setAdding] = useState(false);
  const r = useLoad(() => api.get<any>(`/api/personnel?employee=${employee}&kind=${kind}`), [employee, kind]);
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Agency"
        title="Personnel"
        sub="Write-ups, counseling, commendations and complaints. Discipline and complaints feed the early warning system automatically."
        actions={
          <button className="btn primary" onClick={() => setAdding(!adding)}>
            New personnel action
          </button>
        }
      />
      {adding && r.data && <NewAction data={r.data} onCancel={() => setAdding(false)} />}
      <div className="row gap wrap">
        <select value={employee} onChange={(e) => setEmployee(e.target.value)} aria-label="Filter by employee">
          <option value="">Everyone</option>
          {(r.data?.people ?? []).map((p: any) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Filter by kind">
          <option value="">Every kind</option>
          {Object.entries(r.data?.kinds ?? {}).map(([k, v]: any) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </div>
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.actions.length ? (
        <EmptyState icon="clipboard" title="Nothing here" />
      ) : (
        <div className="card table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Employee</th>
                <th>What</th>
                <th>Date</th>
                <th>By</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {r.data.actions.map((p: any) => (
                <tr key={p.id} className="clickable" onClick={() => navigate(at(`/personnel/${p.id}`))}>
                  <td className="mono">{p.number}</td>
                  <td>{p.employee_name}</td>
                  <td>
                    <Badge tone={kindTone(r.data.kinds[p.kind])}>{r.data.kinds[p.kind]?.label}</Badge> {p.title}
                    {p.finding && <div className="small muted">{r.data.findings[p.finding]}</div>}
                  </td>
                  <td>{day(p.occurred_on ?? p.created_at)}</td>
                  <td>{p.issued_by_name}</td>
                  <td>
                    <Badge tone={PA_STATUS[p.status]![1]}>{PA_STATUS[p.status]![0]}</Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NewAction({ data, onCancel }: { data: any; onCancel: () => void }) {
  const { navigate } = useRouter();
  const [f, setF] = useState({ employeeId: '', kind: 'commendation', title: '', occurredOn: '', policy: '', description: '', actionTaken: '', suspensionDays: '', complainant: '' });
  const a = useAction();
  const complaint = f.kind === 'complaint' || f.kind === 'internal_complaint';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    a.run(async () => {
      const r = await api.post<any>('/api/personnel', { ...f, occurredOn: f.occurredOn || null, suspensionDays: f.suspensionDays ? Number(f.suspensionDays) : null, policy: f.policy || null, actionTaken: f.actionTaken || null, complainant: f.complainant || null });
      navigate(at(`/personnel/${r.id}`));
    });
  };
  return (
    <Card title="New personnel action">
      <form className="stack" onSubmit={submit}>
        <ErrorBox error={a.error} />
        <div className="grid-3">
          <label>
            Employee
            <select value={f.employeeId} required onChange={(e) => setF({ ...f, employeeId: e.target.value })}>
              <option value="">Choose…</option>
              {data.people.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Kind
            <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })}>
              {Object.entries(data.kinds).map(([k, v]: any) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date it happened
            <input type="date" value={f.occurredOn} onChange={(e) => setF({ ...f, occurredOn: e.target.value })} />
          </label>
        </div>
        <label>
          Title
          <input value={f.title} required onChange={(e) => setF({ ...f, title: e.target.value })} placeholder={complaint ? 'e.g. Rude during a traffic stop' : f.kind.includes('commend') || f.kind === 'appreciation' ? 'e.g. Life saved at Ridge Rd crash' : 'e.g. Late for shift three times'} />
        </label>
        {!['commendation', 'appreciation'].includes(f.kind) && (
          <label>
            Policy / rule involved
            <input value={f.policy} onChange={(e) => setF({ ...f, policy: e.target.value })} />
          </label>
        )}
        {complaint && (
          <label>
            Complainant
            <input value={f.complainant} onChange={(e) => setF({ ...f, complainant: e.target.value })} />
          </label>
        )}
        <label>
          What happened
          <textarea rows={5} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
        </label>
        {!complaint && !['commendation', 'appreciation'].includes(f.kind) && (
          <label>
            Action taken / expectations going forward
            <textarea rows={2} value={f.actionTaken} onChange={(e) => setF({ ...f, actionTaken: e.target.value })} />
          </label>
        )}
        {f.kind === 'suspension' && (
          <label>
            Days suspended
            <input type="number" min={0} max={365} step={0.5} value={f.suspensionDays} onChange={(e) => setF({ ...f, suspensionDays: e.target.value })} />
          </label>
        )}
        <div className="row gap">
          <button className="btn primary" disabled={a.busy}>
            Save draft
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </Card>
  );
}

export function PersonnelDetail({ id }: { id: string }) {
  const { navigate } = useRouter();
  const { me } = useAuth();
  const r = useLoad(() => api.get<any>(`/api/personnel/${id}`), [id]);
  const a = useAction();
  const [ack, setAck] = useState({ name: '', comment: '' });
  const [finding, setFinding] = useState('');
  const v = r.data;
  if (!v) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  const p = v.action;
  const k = v.kinds[p.kind];
  const mine = p.employee_id === me?.user.id;
  return (
    <div className="ops stack">
      <PageHeader crumb={mine ? crumb('My record', '/personnel/me', navigate) : crumb('Personnel', '/personnel', navigate)} title={`${p.number} · ${p.title}`} sub={`${k.label} · ${p.employee_name}`} actions={<Badge tone={PA_STATUS[p.status]![1]}>{PA_STATUS[p.status]![0]}</Badge>} />
      <ErrorBox error={a.error} />
      <Card>
        <dl className="kv">
          <dt>Employee</dt>
          <dd>{p.employee_name}</dd>
          <dt>Kind</dt>
          <dd>
            <Badge tone={kindTone(k)}>{k.label}</Badge>
          </dd>
          {p.occurred_on && (
            <>
              <dt>Date</dt>
              <dd>{day(p.occurred_on)}</dd>
            </>
          )}
          {p.policy && (
            <>
              <dt>Policy</dt>
              <dd>{p.policy}</dd>
            </>
          )}
          {p.complainant && (
            <>
              <dt>Complainant</dt>
              <dd>{p.complainant}</dd>
            </>
          )}
          <dt>What happened</dt>
          <dd className="pre">{p.description || '—'}</dd>
          {p.action_taken && (
            <>
              <dt>Action taken</dt>
              <dd className="pre">{p.action_taken}</dd>
            </>
          )}
          {p.suspension_days != null && (
            <>
              <dt>Days suspended</dt>
              <dd>{p.suspension_days}</dd>
            </>
          )}
          {p.finding && (
            <>
              <dt>Finding</dt>
              <dd>{v.findings[p.finding]}</dd>
            </>
          )}
          <dt>Written by</dt>
          <dd>
            {p.issued_by_name}
            {p.issued_at ? `, issued ${when(p.issued_at)}` : ''}
          </dd>
          {p.ack_at && (
            <>
              <dt>Acknowledged</dt>
              <dd>
                {p.ack_name}, {when(p.ack_at)}
                {p.ack_comment && <div className="small pre">Employee’s comment: {p.ack_comment}</div>}
              </dd>
            </>
          )}
          {p.void_reason && (
            <>
              <dt>Void</dt>
              <dd>{p.void_reason}</dd>
            </>
          )}
        </dl>
      </Card>
      {v.canIssue && (
        <Card title="Issue it">
          <p className="small">The employee gets a message and can read it{k.tone === 'good' ? '' : ', and has to acknowledge receiving it'}.</p>
          <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post(`/api/personnel/${id}/issue`, {}), r.reload()))}>
            Issue to {p.employee_name}
          </button>
        </Card>
      )}
      {v.canFinding && (
        <Card title="Investigation finding">
          <div className="row gap wrap">
            <select value={finding || p.finding || 'pending'} onChange={(e) => setFinding(e.target.value)} aria-label="Finding">
              {Object.entries(v.findings).map(([fk, l]: any) => (
                <option key={fk} value={fk}>
                  {l}
                </option>
              ))}
            </select>
            <button className="btn" disabled={a.busy || !finding} onClick={() => a.run(async () => (await api.post(`/api/personnel/${id}`, { finding }), setFinding(''), r.reload()))}>
              Save finding
            </button>
          </div>
        </Card>
      )}
      {v.canAck && (
        <Card title="Acknowledge">
          <form
            className="stack sm"
            onSubmit={(e) => {
              e.preventDefault();
              a.run(async () => (await api.post(`/api/personnel/${id}/acknowledge`, { name: ack.name, comment: ack.comment || null }), r.reload()));
            }}
          >
            <p className="small">Signing means you received it, not that you agree. You can add your side below.</p>
            <label>
              Type your name to sign
              <input value={ack.name} required onChange={(e) => setAck({ ...ack, name: e.target.value })} />
            </label>
            <label>
              Your comment (optional)
              <textarea rows={3} value={ack.comment} onChange={(e) => setAck({ ...ack, comment: e.target.value })} />
            </label>
            <div>
              <button className="btn primary" disabled={a.busy}>
                Acknowledge
              </button>
            </div>
          </form>
        </Card>
      )}
      {v.canVoid && (
        <div>
          <button className="btn link small" onClick={() => {
            const reason = prompt('Why is this being voided?');
            if (reason) a.run(async () => (await api.post(`/api/personnel/${id}/void`, { reason }), r.reload()));
          }}>
            Void this record
          </button>
        </div>
      )}
    </div>
  );
}

export function MyRecord() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/personnel/me'), []);
  return (
    <div className="ops stack">
      <PageHeader crumb="Me" title="My record" sub="Commendations, counseling and write-ups issued to you." />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !r.data ? (
        <Spinner />
      ) : !r.data.actions.length ? (
        <EmptyState icon="clipboard" title="Nothing on your record" />
      ) : (
        <div className="fi-list">
          {r.data.actions.map((p: any) => (
            <button key={p.id} type="button" className="card fi-item" onClick={() => navigate(at(`/personnel/${p.id}`))}>
              <div className="fi-top">
                <span>
                  <Badge tone={kindTone(r.data.kinds[p.kind])}>{r.data.kinds[p.kind]?.label}</Badge> <strong>{p.title}</strong>
                </span>
                <span className="small muted">
                  {when(p.issued_at)} · {p.issued_by_name}
                </span>
              </div>
              {p.status === 'issued' && <span className="small" style={{ color: 'var(--warn)' }}>Open it to acknowledge you received it</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Early warning ──────────────────────────────────────────────────────────
export function EwsDashboard() {
  const { navigate } = useRouter();
  const { can } = useAuth();
  const r = useLoad(() => api.get<any>('/api/ews'), []);
  const v = r.data;
  const open = (v?.alerts ?? []).filter((x: any) => x.status === 'open');
  return (
    <div className="ops stack">
      <PageHeader
        crumb="Command staff"
        title="Early warning"
        sub="Officers whose uses of force, complaints or discipline cross the agency’s thresholds (Act 218). Officers don’t see this page."
        actions={
          can('admin.config') && (
            <button className="btn" onClick={() => navigate(at('/ews/settings'))}>
              Thresholds
            </button>
          )
        }
      />
      {r.error ? (
        <ErrorBox error={r.error} />
      ) : !v ? (
        <Spinner />
      ) : (
        <>
          <div className="ops-kpis">
            <KPI n={open.length} label="Alerts to review" tone={open.length ? 'bad' : undefined} />
            <KPI n={v.officers.filter((o: any) => o.flagged.length).length} label="Officers over a threshold" />
            <KPI n={v.officers.filter((o: any) => !o.flagged.length && o.near.length).length} label="One away from a threshold" />
          </div>
          {open.length > 0 && (
            <Card title="Alerts to review">
              <div className="stack">
                {open.map((x: any) => (
                  <AlertReview key={x.id} x={x} v={v} onDone={r.reload} />
                ))}
              </div>
            </Card>
          )}
          {!v.officers.length ? (
            <EmptyState icon="check" title="Nothing on anyone in the windows set" />
          ) : (
            <div className="card table-wrap">
              <table className="table ews-table">
                <thead>
                  <tr>
                    <th>Officer</th>
                    {Object.entries(v.indicators).map(([k, m]: any) => (
                      <th key={k} title={`${m.label}: ${v.thresholds[k].count} in ${v.thresholds[k].days} days`}>
                        {m.label}
                        <div className="small muted">
                          {v.thresholds[k].count}/{v.thresholds[k].days}d
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {v.officers.map((o: any) => (
                    <tr key={o.id} className="clickable" onClick={() => navigate(at(`/ews/officers/${o.id}`))}>
                      <td>
                        <strong>{o.name}</strong>
                        {o.open > 0 && (
                          <div>
                            <Badge tone="bad">{o.open} open</Badge>
                          </div>
                        )}
                      </td>
                      {Object.keys(v.indicators).map((k) => (
                        <td key={k} className={o.flagged.includes(k) ? 'ews-hit' : o.near.includes(k) ? 'ews-near' : ''}>
                          {o.counts[k] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {v.alerts.some((x: any) => x.status === 'reviewed') && (
            <Card title="Reviewed alerts">
              <ul className="pio-hist">
                {v.alerts
                  .filter((x: any) => x.status === 'reviewed')
                  .map((x: any) => (
                    <li key={x.id}>
                      <strong>
                        {x.officer_name} · {v.indicators[x.indicator]?.label} ({x.count})
                      </strong>
                      <span className="small muted">
                        {' '}
                        · {v.outcomes[x.outcome]} · {x.reviewed_by_name}, {when(x.reviewed_at)}
                      </span>
                      {x.review_note && <div className="small pre">{x.review_note}</div>}
                    </li>
                  ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function AlertReview({ x, v, onDone }: { x: any; v: any; onDone: () => void }) {
  const [outcome, setOutcome] = useState('no_action');
  const [note, setNote] = useState('');
  const a = useAction();
  return (
    <div className="ews-alert">
      <div>
        <strong>{x.officer_name}</strong> — {x.count} {v.indicators[x.indicator]?.label.toLowerCase()} in {x.window_days} days (threshold {x.threshold}) <span className="small muted">· {when(x.created_at)}</span>
        <ul className="small">
          {(x.items ?? []).map((i: any) => (
            <li key={i.id}>
              {i.label} · {day(i.at)}
            </li>
          ))}
        </ul>
      </div>
      <ErrorBox error={a.error} />
      <div className="row gap wrap">
        <select value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="What was done">
          {Object.entries(v.outcomes).map(([k, l]: any) => (
            <option key={k} value={k}>
              {l}
            </option>
          ))}
        </select>
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you found and did" aria-label="Review note" style={{ flex: 1, minWidth: 220 }} />
        <button className="btn primary" disabled={a.busy || !note.trim()} onClick={() => a.run(async () => (await api.post(`/api/ews/alerts/${x.id}/review`, { outcome, note }), onDone()))}>
          Record review
        </button>
      </div>
    </div>
  );
}

export function EwsOfficer({ id }: { id: string }) {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>(`/api/ews/officers/${id}`), [id]);
  const v = r.data;
  if (!v) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Early warning', '/ews', navigate)} title={v.officer.name} sub={[v.officer.title, v.officer.badge_number].filter(Boolean).join(' · ')} />
      <Card title="Indicator history (2 years)">
        {!v.events.length ? (
          <p className="small muted">Nothing.</p>
        ) : (
          <ul className="pio-hist">
            {v.events.map((e: any) => (
              <li key={e.id}>
                <strong>{e.label}</strong> <span className="small muted">· {v.indicators[e.indicator]?.label} · {day(e.at)}</span>
              </li>
            ))}
          </ul>
        )}
      </Card>
      {v.commendations.length > 0 && (
        <Card title="Commendations">
          <ul className="pio-hist">
            {v.commendations.map((c: any) => (
              <li key={c.id}>
                <strong>{c.title}</strong> <span className="small muted">· {day(c.issued_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card title="Alerts">
        {!v.alerts.length ? (
          <p className="small muted">None.</p>
        ) : (
          <ul className="pio-hist">
            {v.alerts.map((x: any) => (
              <li key={x.id}>
                <strong>
                  {v.indicators[x.indicator]?.label}: {x.count} in {x.window_days} days
                </strong>{' '}
                <span className="small muted">· {when(x.created_at)}</span>
                <div className="small">{x.status === 'open' ? <Badge tone="bad">Open</Badge> : `${v.outcomes[x.outcome]} — ${x.reviewed_by_name}: ${x.review_note}`}</div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function EwsSettings() {
  const { navigate } = useRouter();
  const r = useLoad(() => api.get<any>('/api/ews/settings'), []);
  const [t, setT] = useState<any>(null);
  const [saved, setSaved] = useState(false);
  const a = useAction();
  useEffect(() => {
    if (r.data && !t) setT(r.data.thresholds);
  }, [r.data, t]);
  if (!r.data || !t) return <div className="ops stack">{r.error ? <ErrorBox error={r.error} /> : <Spinner />}</div>;
  return (
    <div className="ops stack">
      <PageHeader crumb={crumb('Early warning', '/ews', navigate)} title="Early warning thresholds" sub="An alert goes to command staff when an officer reaches this many in this many days. 0 turns an indicator off." />
      <ErrorBox error={a.error} />
      {saved && <Notice tone="ok">Saved.</Notice>}
      <Card>
        <div className="stack sm">
          {Object.entries(r.data.indicators).map(([k, m]: any) => (
            <div key={k} className="row gap wrap ews-set">
              <strong style={{ minWidth: 260 }}>{m.label}</strong>
              <label>
                How many
                <input type="number" min={0} max={50} value={t[k].count} onChange={(e) => setT({ ...t, [k]: { ...t[k], count: Number(e.target.value) } })} />
              </label>
              <label>
                In how many days
                <input type="number" min={7} max={1095} value={t[k].days} onChange={(e) => setT({ ...t, [k]: { ...t[k], days: Number(e.target.value) } })} />
              </label>
            </div>
          ))}
          <div>
            <button className="btn primary" disabled={a.busy} onClick={() => a.run(async () => (await api.post('/api/ews/settings', { thresholds: t }), setSaved(true)))}>
              Save thresholds
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}

